// Factory for creating and managing audio worklets
import type { AudioTrackLength } from '../../src/types/audio-length.js';

export interface WorkletFactoryOptions {
  audioContext: AudioContext;
  playbackAudioWorkletUrl: string;
  signalsmithStretchAudioWorkletUrl: string;
  promisedWebAssemblyModule: Promise<WebAssembly.Module>;
  workerUrl: string;
}

export interface WorkletPair {
  playbackWorklet: AudioWorkletNode;
  stretchWorklet: AudioWorkletNode;
  worker: Worker;
  workerId: number;
}

export class WorkletFactory {
  private audioContext: AudioContext;
  private playbackAudioWorkletUrl: string;
  private signalsmithStretchAudioWorkletUrl: string;
  private promisedWebAssemblyModule: Promise<WebAssembly.Module>;
  private workerUrl: string;
  private workletModulesLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(options: WorkletFactoryOptions) {
    this.audioContext = options.audioContext;
    this.playbackAudioWorkletUrl = options.playbackAudioWorkletUrl;
    this.signalsmithStretchAudioWorkletUrl = options.signalsmithStretchAudioWorkletUrl;
    this.promisedWebAssemblyModule = options.promisedWebAssemblyModule;
    this.workerUrl = options.workerUrl;
  }

  /**
   * Load audio worklet modules
   */
  async loadWorkletModules(): Promise<void> {
    if (this.workletModulesLoaded) {
      return;
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.performWorkletLoading();

    try {
      await this.loadingPromise;
      this.workletModulesLoaded = true;
    } catch (error) {
      this.loadingPromise = null;
      throw error;
    }
  }

  /**
   * Perform the actual worklet loading
   */
  private async performWorkletLoading(): Promise<void> {
    const modules = [
      this.audioContext.audioWorklet.addModule(this.playbackAudioWorkletUrl),
      this.audioContext.audioWorklet.addModule(this.signalsmithStretchAudioWorkletUrl)
    ];

    await Promise.all(modules).catch(error => {
      console.error('Failed to load audio worklets:', error);
      throw error;
    });
  }

  /**
   * Create a playback worklet
   * @param isTempoSynced Whether this is tempo-synced playback
   * @param trackLength Optional track length with clear terminology
   * @returns Playback AudioWorkletNode
   */
  async createPlaybackWorklet(
    isTempoSynced: boolean,
    trackLength?: AudioTrackLength
  ): Promise<AudioWorkletNode> {
    await this.loadWorkletModules();

    const options: any = {
      outputChannelCount: [2],
      parameterData: {
        playbackRate: isTempoSynced ? 0 : 1 // 0 for tempo sync, 1 for basic playback
      }
    };

    // Add trackLength to processorOptions if provided
    if (trackLength) {
      options.processorOptions = {
        trackLength: {
          syncLength: trackLength.syncLength,
          playbackLength: trackLength.playbackLength,
          metadata: {
            encoderDelay: trackLength.metadata.encoderDelay,
            endPadding: trackLength.metadata.endPadding
          }
        }
      };
    }

    // Use simple playback processor (now with SIMD optimization built-in)
    const processorName = 'simple-playback-processor';

    const playbackWorklet = new AudioWorkletNode(this.audioContext, processorName, options);

    return playbackWorklet;
  }

  /**
   * Create a stretch worklet
   * @returns Stretch AudioWorkletNode
   */
  async createStretchWorklet(): Promise<AudioWorkletNode> {
    await this.loadWorkletModules();
    const wasmModule = await this.promisedWebAssemblyModule;

    const stretchWorklet = new AudioWorkletNode(
      this.audioContext,
      'signalsmith-stretch-audio-worklet-processor',
      {
        parameterData: { playbackRate: 0 },
        processorOptions: { module: wasmModule }
      }
    );

    // Wait for stretch worklet to be ready
    await this.waitForStretchWorkletReady(stretchWorklet);

    return stretchWorklet;
  }

  /**
   * Wait for stretch worklet to signal it's ready
   */
  private async waitForStretchWorkletReady(stretchWorklet: AudioWorkletNode): Promise<void> {
    return new Promise<void>(resolve => {
      stretchWorklet.port.addEventListener(
        'message',
        () => {
          console.log('✅ Stretch worklet ready');
          resolve();
        },
        { once: true }
      );
      stretchWorklet.port.start();
    });
  }

  /**
   * Create a worker for stretch processing
   * @returns Worker instance
   */
  async createWorker(): Promise<Worker> {
    const worker = new Worker(this.workerUrl, { type: 'module' });

    // Send WebAssembly module to worker
    const wasmModule = await this.promisedWebAssemblyModule;
    worker.postMessage(wasmModule);

    return worker;
  }

  /**
   * Connect worklet port to worker
   * @param stretchWorklet Stretch worklet to connect
   * @param worker Worker to connect to
   * @returns Worker ID for tracking
   */
  connectWorkletToWorker(stretchWorklet: AudioWorkletNode, worker: Worker): number {
    const workerId = Math.random(); // Use random ID

    // Transfer the port to the worker
    worker.postMessage([stretchWorklet.port, workerId], [stretchWorklet.port]);
    console.log('🔄 Connected stretch worklet to worker');

    return workerId;
  }

  /**
   * Create a complete worklet pair with worker
   * @param isTempoSynced Whether this is tempo-synced playback
   * @param trackLength Optional track length with clear terminology
   * @returns WorkletPair with all components
   */
  async createWorkletPair(
    isTempoSynced: boolean,
    trackLength?: AudioTrackLength
  ): Promise<WorkletPair> {
    await this.loadWorkletModules();

    // Create all components
    const [playbackWorklet, stretchWorklet, worker] = await Promise.all([
      this.createPlaybackWorklet(isTempoSynced, trackLength),
      this.createStretchWorklet(),
      this.createWorker()
    ]);

    // Connect worklet to worker
    const workerId = this.connectWorkletToWorker(stretchWorklet, worker);

    // Connect audio chain
    playbackWorklet.connect(stretchWorklet);
    console.log('🔊 Connected audio chain: playback → stretch');

    return {
      playbackWorklet,
      stretchWorklet,
      worker,
      workerId
    };
  }

  /**
   * Cleanup a worklet pair
   * @param workletPair The worklet pair to cleanup
   */
  cleanupWorkletPair(workletPair: WorkletPair): void {
    try {
      // Send stop messages
      workletPair.playbackWorklet.port.postMessage('stop');
      workletPair.worker.postMessage(workletPair.workerId); // Tell worker to stop processing this ID

      // Terminate worker
      workletPair.worker.terminate();

      // Disconnect audio nodes
      workletPair.stretchWorklet.disconnect();
      workletPair.playbackWorklet.disconnect();
    } catch (error) {
      console.error('Error cleaning up worklet pair:', error);
    }
  }

  /**
   * Check if worklet modules are loaded
   */
  isLoaded(): boolean {
    return this.workletModulesLoaded;
  }

  /**
   * Get the audio context
   */
  getAudioContext(): AudioContext {
    return this.audioContext;
  }

  /**
   * Send track length to a worklet via port message
   * @param worklet The worklet to send track length to
   * @param trackLength The track length with clear terminology
   */
  sendTrackLengthToWorklet(worklet: AudioWorkletNode, trackLength: AudioTrackLength): void {
    worklet.port.postMessage({
      type: 'setTrackLength',
      trackLength: {
        syncLength: trackLength.syncLength,
        playbackLength: trackLength.playbackLength,
        metadata: {
          encoderDelay: trackLength.metadata.encoderDelay,
          endPadding: trackLength.metadata.endPadding
        }
      }
    });
  }
}
